import { useCallback, useMemo, useState } from 'react';

import { Platform, ScrollView } from 'react-native';

import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { XStack, YStack, useMedia } from 'tamagui';
import { useShallow } from 'zustand/react/shallow';

import { HeadingL, Icon, LabelM } from '@south-street-app/atoms';
import { ScreenWrapper } from '@south-street-app/molecules';
import { useChatStore } from '@south-street-app/stores';
import { refreshUnreadCounts } from '@south-street-app/utils';

import { ArchivedChatsModal } from './ArchivedChatsModal';
import { ConversationRow } from './ConversationRow';
import { useConversationPreview } from '../ChatScreen/useConversationPreview';
import { SupportConversationContent } from '../SupportChatScreen/SupportConversationContent';
import { UpdatesConversationContent } from '../UpdatesChatScreen/UpdatesConversationContent';

type ChannelIconProps = {
  iconName: Parameters<typeof Icon>[0]['iconName'];
  backgroundColor: string;
  iconSize?: Parameters<typeof Icon>[0]['size'];
};

const ChannelIcon = ({ iconName, backgroundColor, iconSize = '$5' }: ChannelIconProps) => (
  <YStack
    alignItems={'center'}
    backgroundColor={backgroundColor as '$brand500'}
    borderRadius={'$full'}
    height={'$9'}
    justifyContent={'center'}
    width={'$9'}
  >
    <Icon color={'$mono100'} iconName={iconName} size={iconSize} />
  </YStack>
);

type DesktopSelection = { kind: 'support' } | { kind: 'updates' };

const pressStyle = { opacity: 0.8 };

const ArchivedChatsButton = ({ onPress }: { onPress: () => void }) => (
  <XStack
    alignItems={'center'}
    borderColor={'$mono300'}
    borderRadius={12} // no exact token: between $m (8) and $true (16)
    borderWidth={1}
    minHeight={'$14'}
    justifyContent={'space-between'}
    margin={'$4'}
    onPress={onPress}
    paddingHorizontal={'$4'}
    pressStyle={pressStyle}
    {...(Platform.OS === 'web' ? { cursor: 'pointer' } : undefined)}
  >
    <LabelM color={'$mono900'} fontWeight={'$2'}>
      {"Archived Chats"}
    </LabelM>
    <Icon color={'$mono500'} iconName={'chevronRightIcon'} size={'$4'} />
  </XStack>
);

const ChatInboxScreen = () => {
  const { desktop } = useMedia();
  const navigation = useNavigation();
  const [desktopSelection, setDesktopSelection] = useState<DesktopSelection>({
    kind: 'support',
  });
  const [archivedModalVisible, setArchivedModalVisible] = useState(false);
  const {
    supportConversation,
    supportUnreadCount,
    updatesConversation,
    updatesUnreadCount,
    inboxRefreshKey,
  } = useChatStore(
    useShallow((state) => ({
      supportConversation: state.supportConversation,
      updatesConversation: state.updatesConversation,
      supportUnreadCount: state.supportUnreadCount,
      updatesUnreadCount: state.updatesUnreadCount,
      inboxRefreshKey: state.inboxRefreshKey,
    })),
  );
  const supportPreview = useConversationPreview(
    supportConversation,
    inboxRefreshKey,
  );
  const updatesPreview = useConversationPreview(
    updatesConversation,
    inboxRefreshKey,
  );

  useFocusEffect(
    useCallback(() => {
      useChatStore.getState().bumpInboxRefreshKey();

      refreshUnreadCounts().catch(() => undefined);
    }, []),
  );

  const activeDesktopConversation = useMemo(() => {
    if (desktopSelection.kind === 'updates') {
      return (
        <UpdatesConversationContent
          conversation={updatesConversation}
          inlineTitle={'Updates'}
        />
      );
    }

    return (
      <SupportConversationContent
        conversation={supportConversation}
        inlineTitle={'Chat'}
      />
    );
  }, [desktopSelection.kind, supportConversation, updatesConversation]);

  const handleOpenSupport = useCallback(() => {
    if (desktop) {
      setDesktopSelection({ kind: 'support' });

      return;
    }

    navigation.navigate('SupportChatScreen');
  }, [desktop, navigation]);

  const handleOpenUpdates = useCallback(() => {
    if (desktop) {
      setDesktopSelection({ kind: 'updates' });

      return;
    }

    navigation.navigate('UpdatesChatScreen');
  }, [desktop, navigation]);

  const handleOpenArchived = useCallback(() => {
    if (desktop) {
      setArchivedModalVisible(true);

      return;
    }

    navigation.navigate('ArchivedChatsScreen');
  }, [desktop, navigation]);

  const handleArchivedConversationPress = useCallback(
    (uniqueName: string) => {
      setArchivedModalVisible(false);

      navigation.navigate('ArchivedChatScreen', { uniqueName });
    },
    [navigation],
  );

  const inboxContent = (
    <YStack
      backgroundColor={'$mono100'}
      borderColor={'$mono300'}
      borderRadius={desktop ? '$true' : undefined}
      borderWidth={desktop ? 1 : 0}
      flex={desktop ? undefined : 1}
      flexShrink={desktop ? 0 : undefined}
      overflow={'hidden'}
      width={desktop ? '$80' : undefined}
    >
      <HeadingL
        color={'$mono900'}
        paddingHorizontal={'$4'}
        paddingTop={'$4'}
        paddingBottom={'$2'}
      >
        {"Inbox"}
      </HeadingL>

      <ScrollView style={{ flex: 1 }}>
        <ConversationRow
          avatar={
            <ChannelIcon backgroundColor={'$brand500'} iconName={'southStreetIcon'} iconSize={'$4'} />
          }
          onPress={handleOpenSupport}
          selected={desktopSelection.kind === 'support'}
          subtitle={
            supportPreview?.body ?? 'Message South Street support'
          }
          timestamp={
            supportPreview?.dateCreated ?? supportConversation?.lastMessage?.dateCreated
          }
          title={'Trading Desk'}
          unreadCount={supportUnreadCount}
        />
        <YStack
          borderBottomColor={'$mono300'}
          borderBottomWidth={1}
          marginHorizontal={'$4'}
        />
        <ConversationRow
          avatar={
            <ChannelIcon backgroundColor={'$mono300'} iconName={'trendingIcon'} />
          }
          onPress={handleOpenUpdates}
          selected={desktopSelection.kind === 'updates'}
          subtitle={updatesPreview?.body ?? 'Broadcast updates from South Street'}
          timestamp={
            updatesPreview?.dateCreated ?? updatesConversation?.lastMessage?.dateCreated
          }
          title={'Updates'}
          unreadCount={updatesUnreadCount}
        />
      </ScrollView>

      <ArchivedChatsButton onPress={handleOpenArchived} />
    </YStack>
  );

  if (!desktop) {
    return <ScreenWrapper>{inboxContent}</ScreenWrapper>;
  }

  return (
    <ScreenWrapper>
      <YStack backgroundColor={'$mono200'} flex={1} padding={'$4'}>
        <XStack flex={1} gap={'$4'}>
          {inboxContent}
          <YStack
            backgroundColor={'$mono100'}
            borderColor={'$mono300'}
            borderRadius={'$true'}
            borderWidth={1}
            flex={1}
            overflow={'hidden'}
          >
            {activeDesktopConversation}
          </YStack>
        </XStack>
      </YStack>

      <ArchivedChatsModal
        onClose={() => setArchivedModalVisible(false)}
        onPressConversation={handleArchivedConversationPress}
        visible={archivedModalVisible}
      />
    </ScreenWrapper>
  );
};

export { ChatInboxScreen };
